import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { IconButton } from './IconButton';

// Kebab ("…") trigger + a simple dropdown of actions. Unlike Dropdown (a
// value-select with a selected option), every item here just runs an onClick —
// use it for per-row overflow actions like Revoke/Delete. Shares Dropdown's
// outside-click / Escape dismissal and elevated-panel styling.

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  /** Renders the item in the danger color (destructive actions). */
  danger?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible label for the kebab trigger. */
  ariaLabel?: string;
  /** Trigger size, forwarded to IconButton. `lg` matches labeled controls. */
  size?: 'sm' | 'lg';
  disabled?: boolean;
  className?: string;
}

export function ActionMenu({
  items,
  ariaLabel = 'More actions',
  size = 'sm',
  disabled,
  className,
}: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={ref} className={clsx('relative inline-flex', className)}>
      <IconButton
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        size={size}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        icon={<DotsThreeVertical size={size === 'lg' ? 22 : 20} weight="bold" />}
      />
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-1.5 z-10 min-w-40 bg-elevated border border-border rounded-lg shadow-lg py-1"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className={clsx(
                'w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-overlay disabled:opacity-40 disabled:hover:bg-transparent',
                item.danger ? 'text-red' : 'text-fg-secondary',
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
